/**
 * viewportShadePlot — shared one-truth for the VIEWPORT shade-plot mode
 * (DXF group 170) UI label + audit label.
 *
 * Shade plot is a PLOT-TIME-ONLY property: it controls how a viewport renders
 * when the layout is PLOTTED, not the on-screen display, so there is no
 * scene/render path tied to it. This module owns ONLY the mode → i18n-key
 * mapping so the ViewportInspector dropdown (component layer) and the
 * geometryShellViewportAudit emitter (state layer) render the SAME human label
 * for a given mode without duplicating the table (jscpd one-truth).
 *
 * AutoCAD / ezdxf reference (DXF VIEWPORT group 170):
 *   0 = As Displayed, 1 = Wireframe, 2 = Hidden, 3 = Rendered.
 * Modes 4 (Visual Style) / 5 (Render Preset) reference a named visual-style /
 * render-preset object handle (DXF 348 / 333) the data model does not yet carry
 * — a documented follow-up scope
 * (docs/test-gaps/viewport-shadeplot-visualstyle-modes.md). An imported file may
 * still carry a raw stored 4/5; `shadePlotModeLabel` renders such an
 * out-of-range value via the `vp.inspector.shadeplot.other` fallback ("Other
 * (N)") so the UI/audit never corrupt or crash on it.
 */

import type { MessageKey } from "../i18n";

/** Translate-fn shape shared by the pure `t()` and the `useT()` hook result. */
export type ShadePlotTranslate = (
  key: MessageKey,
  vars?: Record<string, string | number>,
) => string;

/** The supported authoring range (0..3) the contract constrains writes to. */
export const SHADE_PLOT_MODES = [0, 1, 2, 3] as const;

/** Canonical mode → i18n key map for the four base (authorable) modes. */
const SHADE_PLOT_MODE_KEYS: Record<number, MessageKey> = {
  0: "vp.inspector.shadeplot.as_displayed",
  1: "vp.inspector.shadeplot.wireframe",
  2: "vp.inspector.shadeplot.hidden",
  3: "vp.inspector.shadeplot.rendered",
};

/**
 * Canonical ENGLISH label for the four base modes — for the locale-independent
 * EntityPreview property-string bag (`buildViewportPreview`), which is NOT
 * translated (the preview is a domain string used by CHPROP/MATCHPROP, asserted
 * verbatim in tests). The ViewportInspector dropdown instead renders the
 * LOCALIZED label via `shadePlotModeLabel(mode, t)`; both share the single
 * `SHADE_PLOT_MODES` identity mapping below — only the rendering differs.
 *
 * `As displayed` keeps the historical preview casing (lowercase "d") so existing
 * CHPROP/MATCHPROP fixtures stay valid; the dropdown's localized EN uses
 * "As Displayed" (capital D) independently.
 */
const SHADE_PLOT_MODE_ENGLISH: Record<number, string> = {
  0: "As displayed",
  1: "Wireframe",
  2: "Hidden",
  3: "Rendered",
};

/**
 * Project a shade-plot mode to its canonical ENGLISH preview label. Modes 0..3
 * use their named English label; any other value (imported 4 Visual Style / 5
 * Render Preset or unexpected number) is shown verbatim as `Other (N)` so the
 * preview never collapses 4/5 to a base label (which would re-introduce the
 * fidelity lie this helper exists to remove).
 */
export function shadePlotModeEnglishLabel(mode: number): string {
  return SHADE_PLOT_MODE_ENGLISH[mode] ?? `Other (${mode})`;
}

/**
 * Resolve a shade-plot mode to its localized label. Modes 0..3 use their named
 * label; any other (imported 4/5 or unexpected value) falls back to the "Other
 * (N)" label carrying the raw number so the value is shown verbatim, never
 * corrupted.
 */
export function shadePlotModeLabel(
  mode: number,
  translate: ShadePlotTranslate,
): string {
  const key = SHADE_PLOT_MODE_KEYS[mode];
  if (key) return translate(key);
  return translate("vp.inspector.shadeplot.other", { mode });
}
